import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isInappropriateUsername } from "../../utils/usernameValidation";

interface AddTeamProps {
  isOpen: boolean;
  onClose: () => void;
  onAddTeam: (teamName: string) => void;
  teamNumber?: number;
  initialTeamName?: string;
  existingTeamNames?: string[]; // Array of existing team names to check for duplicates
  editingTeamIndex?: number | null; // Index of team being edited (to exclude from duplicate check)
}

// Modal Component
const Modal = ({ isOpen, onClose, children, isEditing }: { isOpen: boolean; onClose: () => void; children: React.ReactNode; isEditing?: boolean }) => {
  const { t } = useTranslation();
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Trigger animation after render
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      // Delay unmounting to allow exit animation
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-4 z-50 transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Modal Content */}
      <div className={`modal bg-white-500 lg:rounded-[1.5vw] rounded-3xl 2xl:max-w-[26.042vw] max-w-[400px] w-full relative lg:border-[0.2vw] border-3 border-primary auth-page lg:max-h-[calc(100dvh-11%)] max-h-[calc(100dvh-40px)] xl:w-full flex flex-col overflow-hidden transition-all duration-300 ${isAnimating ? 'animate-[slideUp_0.3s_ease-out] opacity-100' : 'opacity-0 translate-y-4'
        }`}>
        {/* Header */}
        <div className="2xl:px-[2vw] md:px-8 px-4 2xl:pt-[2vw] md:pt-8 pt-6 flex-shrink-0">
          <div className="flex 2xl:gap-[1vw] gap-2 items-start justify-between w-full lg:mb-[1vw] mb-3">
            <h1 className="2xl:text-[1.563vw]/[1.7vw] xl:text-3xl/tight text-2xl/6 font-semibold text-primary-orange">
              {isEditing ? t("createGame.editTeam") : t("createGame.addTeamName")}
            </h1>
            {/* Close Button */}
            <button
              onClick={onClose}
              className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300 outline-none"
            >
              <svg
                className="w-full h-full"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                  fill="#333938"
                />
              </svg>
            </button>
          </div>
          {/* Divider */}
          <div className="text-center">
            <hr className="border-0 2xl:h-[0.053vw] h-[1px] w-full [background:linear-gradient(90deg,rgba(11,51,105,0)_0%,#7364FF_50%,rgba(11,51,105,0)_100%)]" />
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto hide-scroll 2xl:px-[2vw] md:px-8 px-4 2xl:py-[1.4vw] md:py-6 py-4">
          {children}
        </div>
      </div>
    </div>
  );
};

// Main Component
function AddTeam({
  isOpen,
  onClose,
  onAddTeam,
  teamNumber,
  initialTeamName = "",
  existingTeamNames = [],
  editingTeamIndex = null,
}: AddTeamProps) {
  const { t } = useTranslation();
  const [teamName, setTeamName] = useState("");
  const [duplicateError, setDuplicateError] = useState("");
  const teamNameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTeamName(initialTeamName || "");
      setDuplicateError("");
      // Focus input when modal opens so user can type immediately.
      const focusTimer = setTimeout(() => {
        teamNameInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(focusTimer);
    } else {
      setTeamName("");
      setDuplicateError("");
    }
  }, [isOpen, initialTeamName]);

  const checkDuplicate = (name: string): boolean => {
    const trimmedName = name.trim().toLowerCase();
    if (!trimmedName) return false;

    // Filter out the current team being edited from the check
    const otherTeamNames = existingTeamNames
      .filter((_, index) => editingTeamIndex === null || index !== editingTeamIndex)
      .map(n => n.trim().toLowerCase());

    return otherTeamNames.includes(trimmedName);
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = teamName.trim();
    if (!trimmed) return;

    if (isInappropriateUsername(trimmed)) {
      setDuplicateError(t("validation.nameInappropriate") || "Team name contains inappropriate or forbidden words");
      return;
    }

    // Check for duplicate team names
    if (checkDuplicate(trimmed)) {
      setDuplicateError(t("createGame.error.duplicateTeamName") || "A team with this name already exists");
      return;
    }

    setDuplicateError("");
    onAddTeam(trimmed);
    setTeamName("");
  };

  const handleTeamNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTeamName(value);
    // Clear duplicate error when user starts typing
    if (duplicateError) {
      setDuplicateError("");
    }
    // Check for duplicate in real-time (optional, can be removed if you only want to check on submit)
    if (value.trim() && checkDuplicate(value)) {
      setDuplicateError(t("createGame.error.duplicateTeamName") || "A team with this name already exists");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isEditing={!!initialTeamName}>
      {/* Avatar */}
      {/* <div
        className="lg:mb-[1.1vw] md:mb-5 mb-4 mx-auto 2xl:w-[5.208vw] xl:w-[90px] w-15 2xl:min-w-[5.208vw] xl:min-w-[90px] -w-15 2xl:h-[5.208vw] xl:h-[90px] h-15 aspect-square overflow-hidden
                bg-[#97A4F2] skew-x-[-10deg]
                2xl:border-[0.104vw] border-[1.5px] 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
      >
        <img alt="" className="w-full h-full object-cover" src={selectedProfile || Avatar3} />
      </div> */}
      <form onSubmit={handleSubmit}>
        <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
          <div
            className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex
    2xl:rounded-[0.26vw] rounded-[5px]
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px]
    transition-all border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]
  `}
          >
            <input
              ref={teamNameInputRef}
              type="text"
              name="teamName"
              placeholder={
                teamNumber === 1
                  ? t("createGame.enterTeamName1")
                  : teamNumber === 2
                    ? t("createGame.enterTeamName2")
                    : teamNumber === 3
                      ? t("createGame.enterTeamName3")
                      : teamNumber === 4
                        ? t("createGame.enterTeamName4")
                        : t("createGame.enterTeamName", { index: teamNumber ?? "" })
              }
              value={teamName}
              onChange={handleTeamNameChange}
              maxLength={20}
              autoFocus
              className={`2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full  lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent`}
            />
          </div>
          {teamName.length === 20 && (
            <p className="text-red-500 lg:text-[0.9vw] text-xs lg:mt-[0.208vw] mt-1 font-bold">
              {t("createGame.error.teamNameCannotExceed20Characters") || "Team name cannot exceed 20 characters"}
            </p>
          )}
          {duplicateError && (
            <p className="text-red-500 lg:text-[0.9vw] text-xs lg:mt-[0.208vw] mt-1 font-bold">
              {duplicateError}
            </p>
          )}
        </div>
        <button
          type="submit"
          className="w-full 2xl:rounded-[0.26vw] rounded-[5px] h-full flex
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] md:text-lg/tight text-base/tight font-bold text-primary-yellow bg-primary-yellow border-black-300 transition-all duration-200 cursor-pointer text-center disabled:opacity-50 disabled:cursor-not-allowed 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
          disabled={!teamName.trim() || !!duplicateError}
        >
          <span className="2xl:pt-[0.7vw] pt-2.5 2xl:m-[0.208vw] m-1 !mt-0 2xl:py-[0.5vw] py-2 lg:px-[5%] px-4 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[0deg] !border-t-0 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[linear-gradient(180deg,#D76A4D_0%,#B35840_101.89%)] w-full h-full">
            <span className="skew-x-[10deg] block">{initialTeamName ? t("createGame.update") || "Update" : t("createGame.teamParticipantModal.add")}</span>
          </span>
        </button>
      </form>
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </Modal>
  );
}

export default AddTeam;
